import React, { useEffect, useState } from 'react';
import { LogOut, Menu, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Separator } from '../ui/separator';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

/** Small count / "new" pill shown at the right of a nav item. */
function NavBadge({ badge }) {
  if (!badge) return null;
  const isNew = badge.tone === 'new';
  return (
    <span
      aria-label={badge.ariaLabel}
      data-testid={badge.testId}
      className={
        isNew
          ? 'ml-auto rounded-full border border-[hsl(var(--brand-apricot))]/25 bg-[hsl(var(--brand-apricot))]/15 px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--brand-apricot))]'
          : 'ml-auto rounded-full border border-border bg-[hsl(var(--brand-mint))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--brand-ink))]'
      }
    >
      {badge.label}
    </span>
  );
}

function NavItem({ item, active, onSelect }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(item.id)}
      data-testid={item.testId || `admin-sidebar-nav-${item.id}`}
      className={[
        'group relative flex w-full min-h-[44px] items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium outline-none',
        'transition-colors duration-200',
        'focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        active
          ? "bg-secondary text-foreground shadow-sm before:content-[''] before:absolute before:left-1 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-[hsl(var(--primary))]"
          : 'text-foreground/75 hover:bg-secondary/70 hover:text-foreground',
      ].join(' ')}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground group-hover:text-foreground'}`} />
      <span className="truncate">{item.label}</span>
      <NavBadge badge={item.badge} />
    </button>
  );
}

function SidebarContent({ groups, activeId, onSelect, roleLabel, agencyLabel, onLogout }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Ptitservices.com" className="h-9 w-auto rounded-lg" />
          <div className="leading-tight min-w-0">
            <p className="text-sm font-semibold tracking-tight font-['Space_Grotesk']">Back-office</p>
            <p className="text-xs text-muted-foreground truncate">Ptitservices.com</p>
          </div>
        </div>
      </div>
      <Separator />
      <ScrollArea className="flex-1 px-3 pb-4">
        <nav aria-label="Navigation admin" role="tablist" className="pb-2">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="px-3 pt-5 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{group.label}</p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavItem key={item.id} item={item} active={item.id === activeId} onSelect={onSelect} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
      <div className="mt-auto border-t border-border px-3 py-3 space-y-2">
        <div className="flex items-center gap-2 px-2">
          <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-mint))] grid place-items-center shrink-0">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground truncate" data-testid="admin-sidebar-role">{roleLabel}</p>
            {agencyLabel && <p className="text-xs text-muted-foreground truncate">{agencyLabel}</p>}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onLogout} data-testid="admin-sidebar-logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
        </Button>
      </div>
    </div>
  );
}

/**
 * Back-office shell: fixed left sidebar on desktop, top bar + Sheet drawer on mobile.
 * The active section component is rendered as `children` inside a readable, centred content column.
 */
export function AdminShell({ groups, activeId, onSelect, title, description, actions, roleLabel, agencyLabel, onLogout, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const select = (id) => {
    onSelect(id);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-dvh bg-background" data-testid="admin-dashboard">
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-40 lg:w-[260px] border-r border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70" data-testid="admin-sidebar">
        <SidebarContent groups={groups} activeId={activeId} onSelect={select} roleLabel={roleLabel} agencyLabel={agencyLabel} onLogout={onLogout} />
      </aside>

      <div className="lg:pl-[260px] min-w-0">
        <header
          data-scrolled={scrolled}
          className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 transition-[box-shadow] duration-200 data-[scrolled=true]:shadow-sm"
        >
          <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden shrink-0" aria-label="Ouvrir le menu" data-testid="admin-topbar-mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[290px] p-0" data-testid="admin-mobile-nav-sheet">
                  <SheetTitle className="sr-only">Navigation admin</SheetTitle>
                  <SidebarContent groups={groups} activeId={activeId} onSelect={select} roleLabel={roleLabel} agencyLabel={agencyLabel} onLogout={onLogout} />
                </SheetContent>
              </Sheet>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold tracking-tight font-['Space_Grotesk'] truncate" data-testid="admin-topbar-page-title">{title}</h1>
                {description && <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground truncate">{description}</p>}
              </div>
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
}
